/**
 * Utilitaires de dates — le fuseau horaire de la société est la référence.
 *
 * RÈGLE : ne plus utiliser `new Date(str + 'T12:00:00')` ni `new Date().toISOString()`
 * directement dans les composants. Passer par ces helpers en fournissant `tz`.
 *
 * Pour obtenir le tz : `const { activeSocieteTimezone } = useSociete();`
 */

/**
 * Formate une chaîne YYYY-MM-DD dans le fuseau horaire donné.
 * @param {string} dateStr  - "2025-01-15" ou ISO complet
 * @param {string} tz       - ex: "Europe/Paris", "America/Martinique"
 * @param {object} opts     - options Intl.DateTimeFormat (surcharge du défaut)
 */
export function fmtDate(dateStr, tz = 'Europe/Paris', opts = {
  day: '2-digit', month: 'short', year: 'numeric',
}) {
  if (!dateStr) return '—';
  const s = String(dateStr).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return String(dateStr);
  const [y, m, d] = s.split('-').map(Number);
  // Midi UTC → date correcte dans tous les fuseaux de UTC-12 à UTC+13
  const date = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  try {
    return new Intl.DateTimeFormat('fr-FR', { timeZone: tz, ...opts }).format(date);
  } catch {
    return new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', ...opts }).format(date);
  }
}

/**
 * Retourne la date d'aujourd'hui (YYYY-MM-DD) dans le fuseau donné.
 * Remplace `new Date().toISOString().slice(0, 10)`.
 */
export function todayInTz(tz = 'Europe/Paris') {
  try {
    // fr-CA utilise le format YYYY-MM-DD
    return new Intl.DateTimeFormat('fr-CA', { timeZone: tz }).format(new Date());
  } catch {
    return new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris' }).format(new Date());
  }
}

/**
 * Retourne la date+heure actuelle au format YYYY-MM-DDTHH:mm dans le fuseau donné.
 * Utilisé pour initialiser les inputs datetime-local.
 */
export function nowInTz(tz = 'Europe/Paris') {
  try {
    const now = new Date();
    const datePart = new Intl.DateTimeFormat('fr-CA', { timeZone: tz }).format(now);
    const timePart = new Intl.DateTimeFormat('fr-FR', {
      timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(now).replace(':', ':');
    return `${datePart}T${timePart}`;
  } catch {
    const iso = new Date().toISOString();
    return iso.slice(0, 16);
  }
}


export function fmtDateTime(isoStr, tz = 'Europe/Paris') {
  if (!isoStr) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      timeZone: tz,
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(isoStr));
  } catch {
    return String(isoStr);
  }
}

/**
 * Ajoute ou soustrait N jours à une date YYYY-MM-DD, retourne YYYY-MM-DD.
 */
export function addDays(dateStr, n) {
  const [y, m, d] = String(dateStr).slice(0, 10).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n, 12, 0, 0));
  return dt.toISOString().slice(0, 10);
}

/**
 * Convertit un timestamp ISO en valeur d'input datetime-local ("YYYY-MM-DDTHH:mm")
 * exprimée dans le fuseau donné.
 */
export function isoToLocalInput(isoStr, tz = 'Europe/Paris') {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    const datePart = new Intl.DateTimeFormat('fr-CA', { timeZone: tz }).format(d);
    const timePart = new Intl.DateTimeFormat('fr-FR', {
      timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(d);
    return `${datePart}T${timePart}`;
  } catch {
    return String(isoStr).slice(0, 16);
  }
}

/**
 * Convertit une valeur d'input datetime-local ("YYYY-MM-DDTHH:mm"), interprétée comme
 * l'heure locale du fuseau tz, en chaîne ISO 8601 avec offset (ex: "2026-09-21T07:00:00+02:00").
 * Compatible avec datetime.fromisoformat côté backend.
 */
export function wallTimeInTzToIso(dtLocal, tz = 'Europe/Paris') {
  if (!dtLocal) return null;
  try {
    const [datePart, timePart] = String(dtLocal).split('T');
    const [y, m, d] = datePart.split('-').map(Number);
    const [hh, mm] = (timePart || '00:00').split(':').map(Number);
    const guess = Date.UTC(y, m - 1, d, hh, mm, 0);
    // Offset du fuseau à un instant donné (via les parties formatées dans tz)
    const offsetAt = (ts) => {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      }).formatToParts(new Date(ts));
      const g = (t) => Number(parts.find(p => p.type === t).value);
      const h = g('hour') === 24 ? 0 : g('hour');
      return Date.UTC(g('year'), g('month') - 1, g('day'), h, g('minute'), g('second')) - ts;
    };
    let off = offsetAt(guess);
    let ts = guess - off;
    const off2 = offsetAt(ts); // 2e passe : transitions heure d'été/hiver
    if (off2 !== off) { ts = guess - off2; off = off2; }
    const offMin = Math.round(off / 60000);
    const sign = offMin >= 0 ? '+' : '-';
    const abs = Math.abs(offMin);
    const pad = (n) => String(n).padStart(2, '0');
    return `${datePart}T${pad(hh)}:${pad(mm)}:00${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
  } catch {
    return dtLocal;
  }
}

/**
 * Liste des fuseaux horaires pour les territoires français.
 */
export const TZ_OPTIONS = [
  { value: 'Europe/Paris',       label: 'Métropole (UTC+1/+2)' },
  { value: 'America/Guadeloupe', label: 'Guadeloupe (UTC-4)' },
  { value: 'America/Martinique', label: 'Martinique (UTC-4)' },
  { value: 'America/Cayenne',    label: 'Guyane française (UTC-3)' },
  { value: 'Indian/Reunion',     label: 'La Réunion (UTC+4)' },
  { value: 'Indian/Mayotte',     label: 'Mayotte (UTC+3)' },
  { value: 'Pacific/Noumea',     label: 'Nouvelle-Calédonie (UTC+11)' },
  { value: 'Pacific/Tahiti',     label: 'Polynésie française (UTC-10)' },
];
